import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link2, Image, PenLine, ChevronDown } from 'lucide-react';
import { ScreenConfig, EditionType } from '@/types/assessment';
import SelectableChip from './SelectableChip';
import VibeChip from './VibeChip';
import CognitiveStyleScreen from './CognitiveStyleScreen';
import PersonalizeScreen from './PersonalizeScreen';
import ResumeUploadScreen from './ResumeUploadScreen';
import ThemePicker from './ThemePicker';

interface ScreenRendererProps {
  screen: ScreenConfig;
  selections: string[];
  openText: string;
  onToggle: (id: string) => void;
  onTextChange: (text: string) => void;
  socialPlatforms: string[];
  edition: EditionType;
}

const bingePlatforms = [
  { id: 'plat-youtube', label: 'YouTube', emoji: '📺', placeholder: 'e.g., Mark Rober, Veritasium, MrBeast, MKBHD...', urlPlaceholder: 'https://youtube.com/@yourchannel' },
  { id: 'plat-podcasts', label: 'Podcasts', emoji: '🎙️', placeholder: 'e.g., How I Built This, The Daily, My First Million...', urlPlaceholder: 'https://open.spotify.com/show/...' },
  { id: 'plat-tiktok', label: 'TikTok / Instagram', emoji: '📱', placeholder: 'e.g., @creators you follow, types of content you see...', urlPlaceholder: 'https://tiktok.com/@yourprofile' },
  { id: 'plat-books', label: 'Books / Articles', emoji: '📚', placeholder: 'e.g., Atomic Habits, Zero to One, blog posts you read...', urlPlaceholder: 'https://goodreads.com/user/...' },
  { id: 'plat-newsletters', label: 'Newsletters', emoji: '📧', placeholder: 'e.g., Morning Brew, The Hustle, Stratechery...', urlPlaceholder: 'https://newsletter-url.com' },
  { id: 'plat-twitter', label: 'X / Twitter', emoji: '💬', placeholder: 'e.g., accounts you follow, topics in your feed...', urlPlaceholder: 'https://x.com/yourprofile' },
];

const contentCategories = [
  { id: 'cat-tech', label: 'Tech' },
  { id: 'cat-business', label: 'Business & Finance' },
  { id: 'cat-creative', label: 'Art, Design & Music' },
  { id: 'cat-gaming', label: 'Gaming & Esports' },
  { id: 'cat-fitness', label: 'Fitness & Health' },
  { id: 'cat-comedy', label: 'Comedy & Entertainment' },
  { id: 'cat-science', label: 'Science & Nature' },
  { id: 'cat-social', label: 'Social Justice & Politics' },
  { id: 'cat-diy', label: 'DIY & How-To' },
  { id: 'cat-fashion', label: 'Fashion & Beauty' },
  { id: 'cat-food', label: 'Food & Cooking' },
  { id: 'cat-sports', label: 'Sports' },
];

type PlatformInputTab = 'url' | 'screenshot' | 'text';

const staggerItem = {
  hidden: { opacity: 0, y: 12 },
  show: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.3 } }),
};

const PlatformCard = ({
  platform,
  expanded,
  onToggleExpand,
  inputs,
  onInputChange,
}: {
  platform: typeof bingePlatforms[0];
  expanded: boolean;
  onToggleExpand: () => void;
  inputs: Record<string, string>;
  onInputChange: (key: string, value: string) => void;
}) => {
  const [activeTab, setActiveTab] = useState<PlatformInputTab>('text');
  const contentId = `platform-content-${platform.id}`;

  return (
    <motion.div
      layout
      className="glass-card overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button
        type="button"
        onClick={onToggleExpand}
        aria-expanded={expanded}
        aria-controls={contentId}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-secondary/30 transition-colors"
      >
        <span className="text-xl" aria-hidden="true">{platform.emoji}</span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{platform.label}</h3>
          <p className="text-xs text-muted-foreground">Share what you follow</p>
        </div>
        <motion.div
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
        </motion.div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div id={contentId} className="px-4 pb-4 space-y-3">
              {/* Input method tabs */}
              <div className="flex gap-1 p-0.5 rounded-lg bg-secondary/40 border border-border/40" role="tablist" aria-label={`${platform.label} input method`}>
                {[
                  { id: 'text' as PlatformInputTab, label: 'Type It', icon: PenLine },
                  { id: 'url' as PlatformInputTab, label: 'Paste URL', icon: Link2 },
                  { id: 'screenshot' as PlatformInputTab, label: 'Screenshot', icon: Image },
                ].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <tab.icon className="w-3 h-3" aria-hidden="true" />
                    {tab.label}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                {activeTab === 'text' && (
                  <motion.div
                    key="text"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.15 }}
                  >
                    <textarea
                      value={inputs[`${platform.id}-text`] || ''}
                      onChange={(e) => onInputChange(`${platform.id}-text`, e.target.value)}
                      placeholder={platform.placeholder}
                      className="w-full px-3 py-2.5 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary min-h-[80px]"
                    />
                  </motion.div>
                )}

                {activeTab === 'url' && (
                  <motion.div
                    key="url"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.15 }}
                    className="space-y-2"
                  >
                    <input
                      type="url"
                      value={inputs[`${platform.id}-url`] || ''}
                      onChange={(e) => onInputChange(`${platform.id}-url`, e.target.value)}
                      placeholder={platform.urlPlaceholder}
                      className="w-full px-3 py-2.5 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-[10px] text-muted-foreground">We extract themes and topics. Nothing is stored or shared.</p>
                  </motion.div>
                )}

                {activeTab === 'screenshot' && (
                  <motion.div
                    key="screenshot"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.15 }}
                  >
                    <label
                      className="flex flex-col items-center justify-center w-full h-24 rounded-xl border-2 border-dashed border-border hover:border-primary/50 cursor-pointer transition-colors bg-card/50"
                      aria-label={`Upload a screenshot of your ${platform.label} feed`}
                    >
                      <Image className="w-6 h-6 text-muted-foreground mb-1.5" aria-hidden="true" />
                      <span className="text-xs text-muted-foreground">Upload a screenshot of your {platform.label} feed</span>
                      <span className="text-[10px] text-muted-foreground mt-0.5">PNG, JPG up to 5MB</span>
                      <input type="file" accept="image/*" className="sr-only" />
                    </label>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ScreenRenderer = ({ screen, selections, openText, onToggle, onTextChange, socialPlatforms, edition }: ScreenRendererProps) => {
  const maxReached = screen.maxSelections ? selections.length >= screen.maxSelections : false;
  const [expandedPlatforms, setExpandedPlatforms] = useState<Record<string, boolean>>({});
  const [platformInputs, setPlatformInputs] = useState<Record<string, string>>({});

  const togglePlatformExpand = (id: string) => {
    setExpandedPlatforms(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handlePlatformInput = (key: string, value: string) => {
    setPlatformInputs(prev => ({ ...prev, [key]: value }));
  };

  switch (screen.type) {
    case 'chips':
      return (
        <div className="space-y-4">
          <motion.div
            className="flex flex-wrap gap-3"
            initial="hidden"
            animate="show"
          >
            {screen.options?.map((option, i) => (
              <motion.div key={option.id} custom={i} variants={staggerItem}>
                <SelectableChip
                  option={option}
                  selected={selections.includes(option.id)}
                  disabled={maxReached && !selections.includes(option.id)}
                  onToggle={onToggle}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      );

    case 'chips-expandable':
      return (
        <motion.div className="space-y-3" initial="hidden" animate="show">
          {screen.options?.map((option, i) => (
            <motion.div key={option.id} custom={i} variants={staggerItem}>
              <SelectableChip
                option={option}
                selected={selections.includes(option.id)}
                disabled={maxReached && !selections.includes(option.id)}
                expandable
                onToggle={onToggle}
              />
            </motion.div>
          ))}
        </motion.div>
      );

    case 'dream-wednesday':
      return (
        <div className="space-y-4">
          <motion.div className="space-y-3" initial="hidden" animate="show">
            {screen.vibeOptions?.map((option, i) => (
              <motion.div key={option.id} custom={i} variants={staggerItem}>
                <VibeChip
                  option={option}
                  selected={selections.includes(option.id)}
                  onToggle={onToggle}
                />
              </motion.div>
            ))}
          </motion.div>
          {screen.allowOpenText && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              <textarea
                value={openText}
                onChange={(e) => onTextChange(e.target.value)}
                placeholder={screen.openTextPlaceholder}
                aria-label={screen.openTextPlaceholder || 'Additional thoughts'}
                className="w-full px-4 py-3 rounded-xl bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
              />
            </motion.div>
          )}
        </div>
      );

    case 'social-scan':
      return (
        <div className="space-y-4">
          {/* Platform cards */}
          <motion.div className="space-y-3" initial="hidden" animate="show">
            {bingePlatforms.map((plat, i) => (
              <motion.div key={plat.id} custom={i} variants={staggerItem}>
                <PlatformCard
                  platform={plat}
                  expanded={!!expandedPlatforms[plat.id]}
                  onToggleExpand={() => togglePlatformExpand(plat.id)}
                  inputs={platformInputs}
                  onInputChange={handlePlatformInput}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Content topic chips */}
          <motion.div
            className="glass-card p-5 space-y-3"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <h3 className="text-sm font-semibold text-foreground">What topics grab you?</h3>
            <p className="text-xs text-muted-foreground">Across all platforms, what do you gravitate toward?</p>
            <motion.div className="flex flex-wrap gap-2 mt-2" initial="hidden" animate="show">
              {contentCategories.map((cat, i) => (
                <motion.button
                  key={cat.id}
                  type="button"
                  custom={i}
                  variants={staggerItem}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onToggle(cat.id)}
                  aria-pressed={selections.includes(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selections.includes(cat.id)
                      ? 'bg-primary/20 border-primary text-primary'
                      : 'bg-card border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  {cat.label}
                </motion.button>
              ))}
            </motion.div>
          </motion.div>

          <motion.p
            className="text-center text-xs text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            This step is totally optional. Skip it if you want.
          </motion.p>
        </div>
      );

    case 'aspirations':
      return (
        <div className="space-y-6">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <h3 className="text-sm font-semibold text-foreground mb-3">Trending Fields</h3>
            <motion.div className="flex flex-wrap gap-3" initial="hidden" animate="show">
              {screen.options?.map((option, i) => (
                <motion.div key={option.id} custom={i} variants={staggerItem}>
                  <SelectableChip
                    option={option}
                    selected={selections.includes(option.id)}
                    disabled={maxReached && !selections.includes(option.id)}
                    onToggle={onToggle}
                  />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.3 }}>
            <h3 className="text-sm font-semibold text-foreground mb-3">What difference do you want to make?</h3>
            <motion.div className="flex flex-wrap gap-3" initial="hidden" animate="show">
              {impactChips.map((chip, i) => (
                <motion.div key={chip.id} custom={i + 4} variants={staggerItem}>
                  <SelectableChip
                    option={chip}
                    selected={selections.includes(chip.id)}
                    disabled={false}
                    onToggle={onToggle}
                  />
                </motion.div>
              ))}
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
              <textarea
                value={openText}
                onChange={(e) => onTextChange(e.target.value)}
                placeholder="Or tell us in your own words..."
                aria-label="Describe what difference you want to make in your own words"
                className="mt-3 w-full px-4 py-3 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary min-h-[60px]"
              />
            </motion.div>
          </motion.div>
        </div>
      );

    case 'barriers':
      return (
        <motion.div className="flex flex-wrap gap-3" initial="hidden" animate="show">
          {screen.options?.map((option, i) => (
            <motion.div key={option.id} custom={i} variants={staggerItem}>
              <SelectableChip
                option={option}
                selected={selections.includes(option.id)}
                disabled={false}
                onToggle={onToggle}
              />
            </motion.div>
          ))}
        </motion.div>
      );

    case 'context':
      return (
        <motion.div className="space-y-4" initial="hidden" animate="show">
          {screen.options?.map((option, i) => {
            const choices = option.description?.split(',').filter(Boolean) || [];
            if (option.id === 'location') {
              return (
                <motion.div key={option.id} custom={i} variants={staggerItem} className="space-y-2">
                  <label htmlFor="context-location" className="text-sm font-medium text-foreground">{option.label}</label>
                  <input
                    id="context-location"
                    type="text"
                    placeholder="e.g., Austin TX, Chicago, Los Angeles..."
                    className="w-full px-4 py-3 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    onChange={(e) => onToggle(`${option.id}:${e.target.value}`)}
                  />
                  <p className="text-xs text-muted-foreground">We'll use this to suggest local programs, meetups, and resources near you.</p>
                </motion.div>
              );
            }
            if (choices.length === 0) return null;
            return (
              <motion.div key={option.id} custom={i} variants={staggerItem} className="space-y-2">
                <label htmlFor={`context-${option.id}`} className="text-sm font-medium text-foreground">{option.label}</label>
                <select
                  id={`context-${option.id}`}
                  className="w-full px-4 py-3 rounded-lg bg-card border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none"
                  onChange={(e) => onToggle(`${option.id}:${e.target.value}`)}
                >
                  <option value="">Select...</option>
                  {choices.map((c) => (
                    <option key={c} value={c.trim()}>{c.trim()}</option>
                  ))}
                </select>
              </motion.div>
            );
          })}
          {screen.allowOpenText && (
            <motion.div custom={3} variants={staggerItem}>
              <textarea
                value={openText}
                onChange={(e) => onTextChange(e.target.value)}
                placeholder={screen.openTextPlaceholder}
                aria-label={screen.openTextPlaceholder || 'Additional context'}
                className="w-full px-4 py-3 rounded-xl bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary min-h-[80px]"
              />
            </motion.div>
          )}
        </motion.div>
      );

    case 'cognitive-style':
      return (
        <CognitiveStyleScreen
          scenarios={screen.cognitiveScenarios || []}
          selections={selections}
          onToggle={onToggle}
        />
      );

    case 'personalize':
      return (
        <PersonalizeScreen
          selections={selections}
          onToggle={onToggle}
          edition={edition}
        />
      );

    case 'resume-upload':
      return (
        <ResumeUploadScreen
          onResumeData={(data) => {
            if (data.skills) {
              data.skills.forEach((skill) => onToggle(`resume-skill:${skill}`));
            }
            if (data.experience) {
              data.experience.forEach((exp) => onToggle(`resume-exp:${exp}`));
            }
          }}
          existingSelections={selections}
        />
      );

    case 'theme-picker':
      return (
        <ThemePicker
          selections={selections}
          onToggle={onToggle}
        />
      );

    default:
      return null;
  }
};

const impactChips = [
  { id: 'impact-inclusion', label: 'Help people feel included' },
  { id: 'impact-solve', label: 'Build things that solve real problems' },
  { id: 'impact-money', label: 'Make money doing what I love' },
  { id: 'impact-art', label: 'Create art that matters' },
  { id: 'impact-planet', label: 'Protect the planet' },
  { id: 'impact-teach', label: 'Teach and mentor others' },
  { id: 'impact-justice', label: 'Fight for justice' },
  { id: 'impact-tech', label: 'Innovate with technology' },
];

export default ScreenRenderer;
